import { FinancieraView } from './costs/FinancieraView';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from './supabase';
import { cn } from './ui/Table';
import { Settings, Calculator, TrendingUp, AlertCircle, BarChart3, PiggyBank, Package, Activity, Target, Zap, Award, ArrowUpRight, ArrowDownRight, Phone, MessageCircle, Receipt, Ship, Tag, Landmark, Truck, Wallet } from 'lucide-react';
import Imports from './Imports';
import { CabifyCostsView } from './costs/CabifyCostsView';
import { Conciliacion } from './Conciliacion';
import { fetchWaMonthUsage, WaMonthUsage } from './WaUsage';
import Investments from './Investments';
import TnImport from './TnImport';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Promotions } from './Promotions';
import { CalcPanel } from './costs/CalcPanel';
import { FijosPanel } from './costs/FijosPanel';
import { ConfigPanel } from './costs/ConfigPanel';
// Pestañas Dashboard · Ventas · Ranking (rediseño uiarc)
import { DataGrid, GridCol } from './ui/DataGrid';
import { Badge } from './ui/arc/badge';
import { Button } from './ui/arc/button';
import { EmptyState } from './ui/arc/empty-state';
import SegmentedControl from './ui/arc/segmented-control';
import { TabHeader, FilterBar, Kpi, KpiChange, Panel, BarList, ChartTooltip, LegendDot, ars, pct, arsCompact, rangeLabel, topShare, VIZ } from './costs/AnalisisUI';
import { AnalisisPortfolio, BAND_META, bandCounts } from './costs/AnalisisPortfolio';
import { ResultadosView } from './costs/ResultadosView';
import { GastosView } from './costs/GastosView';
import { GastoDialog } from './costs/GastoDialog';

// Types
interface CostSettings {
    id: number;
    usd_rate: number;
    eur_rate?: number;
    iva_percent: number;
    acrylic_waste_percent: number;
    use_epic_week_templates?: boolean;
    local_address?: string | null;
    local_hours?: string | null;
    cr_community_link?: string | null;
}

interface Material {
    id: number;
    name: string;
    type: 'simple' | 'acrylic';
    unit_cost: number;
    currency: 'ARS' | 'USD';
    provider_id?: number | null;
    width_mm?: number;
    height_mm?: number;
    thickness_mm?: number;
    weight_kg?: number;
}

interface Provider {
    id: number;
    name: string;
}

interface Expense {
    id: number;
    amount: number;
    provider_id: number | null;
    provider_name: string | null;
    payment_method: string;
    notes: string | null;
    created_at: string;
    created_by: string | null;
}

interface FixedCost {
    id: number;
    name: string;
    amount_ars: number;
}

interface TnProductMaterial {
    id: number;
    tn_product_id: number;
    material_id: number;
    quantity: number;
    width_mm?: number;
    height_mm?: number;
}

interface TnVariantMaterial {
    id: number;
    tn_variant_id: number;
    material_id: number;
    quantity: number;
    width_mm?: number;
    height_mm?: number;
}

interface ProductVariant {
    id: number;
    product_id: number;
    name: string;
    price: number;
    materials: TnVariantMaterial[];
}

interface Product {
    id: number;
    name: string;
    variants: ProductVariant[];
    materials: TnProductMaterial[];
}

function getOrderDisplayId(order: { source?: string; tiendanube_id?: string; id: number }): string {
    if (order.source === 'woocommerce' && order.tiendanube_id) {
        const num = order.tiendanube_id.replace(/^(WC-|BT-)/i, '');
        return `BT-${num}`;
    }
    if ((order.source === 'tiendanube' || order.source === 'mercadolibre') && order.tiendanube_id) {
        return String(order.tiendanube_id);
    }
    return `K-${order.id}`;
}

// Clasifica el medio de pago de CUALQUIER orden (todos los canales) en un bucket para el BI.
// - Canales (TN/WC/ML): manda el gateway real (payment_details.method + gateway_name), porque
//   payment_details.payment_method lo puede pisar el editor manual (NewOrder defaultea 'tarjeta').
// - Órdenes propias (manual/showroom/ig/whatsapp): manda payment_details.payment_method, que es
//   lo que se elige en el formulario.
type PaymentBucket = 'efectivo' | 'transferencia' | 'tarjeta' | 'billetera' | 'otro';

function getPaymentBucket(order: any): PaymentBucket {
    // WooCommerce mete badges HTML en el gateway_name ("Mercado Pago <span…>3 cuotas…</span>")
    const gn = String(order?.gateway_name || '').replace(/<[^>]*>/g, ' ').toLowerCase();
    const method = String(order?.payment_details?.method || '').toLowerCase();
    const manual = String(order?.payment_details?.payment_method || '').toLowerCase();
    const isChannel = order?.source === 'tiendanube' || order?.source === 'woocommerce' || order?.source === 'mercadolibre';

    const byGateway = (): PaymentBucket | null => {
        if (!gn) return null;
        if (/efectivo|cash/.test(gn)) return 'efectivo';
        if (/transferencia|dep[oó]sito|wire/.test(gn)) return 'transferencia';
        if (/account_money|billetera|dinero en cuenta/.test(gn)) return 'billetera';
        if (/tarjeta|cuotas|credit_?card|debit_?card|gocuotas|mercado\s*pago|pago\s*nube/.test(gn)) return 'tarjeta';
        return null;
    };

    if (isChannel) {
        if (method === 'wire_transfer') return 'transferencia';
        if (method === 'credit_card' || method === 'debit_card') return 'tarjeta';
        if (method === 'wallet') return 'billetera';
        // 'custom' = medio offline de TN (Efectivo / Transferencia a convenir); 'other' = ya mutado al cobrar
        const g = byGateway();
        if (g) return g;
        if (method === 'custom') return 'efectivo';
        if (manual === 'efectivo' || manual === 'transferencia' || manual === 'tarjeta') return manual as PaymentBucket;
        return 'otro';
    }

    if (manual === 'efectivo' || manual === 'transferencia' || manual === 'tarjeta') return manual as PaymentBucket;
    if (method === 'custom') return 'efectivo';
    const g = byGateway();
    if (g) return g;
    return 'otro';
}

// Pestañas de Costos & BI agrupadas (antes: 12 botones sueltos en una fila que no entraba).
const COSTS_TABS: { key: string; label: string; group: 'Análisis' | 'Plata' | 'Herramientas'; icon: any }[] = [
    { key: 'dashboard', label: 'Dashboard', group: 'Análisis', icon: Activity },
    { key: 'resultados', label: 'Resultados', group: 'Análisis', icon: TrendingUp },
    { key: 'stats', label: 'Ventas', group: 'Análisis', icon: BarChart3 },
    { key: 'products', label: 'Ranking', group: 'Análisis', icon: Award },
    { key: 'expenses', label: 'Gastos', group: 'Plata', icon: Receipt },
    { key: 'conciliacion', label: 'Transferencias', group: 'Plata', icon: Landmark },
    { key: 'financiera', label: 'Financiera', group: 'Plata', icon: Wallet },
    { key: 'cabify', label: 'Cabify', group: 'Plata', icon: Truck },
    { key: 'fixed', label: 'Costos fijos', group: 'Plata', icon: PiggyBank },
    { key: 'investments', label: 'Inversiones', group: 'Plata', icon: TrendingUp },
    { key: 'imports', label: 'Importaciones', group: 'Plata', icon: Ship },
    { key: 'calculator', label: 'Calculadora', group: 'Herramientas', icon: Calculator },
    { key: 'tn_import', label: 'Import TN', group: 'Herramientas', icon: Package },
    { key: 'settings', label: 'Config', group: 'Herramientas', icon: Settings },
];

export function Costs({ userEmail, orders = [], canjeOrders = [], openExpenseModal: openExpenseModalProp, onExpenseModalClosed }: { userEmail: string | undefined, orders?: any[], canjeOrders?: any[], openExpenseModal?: boolean, onExpenseModalClosed?: () => void }) {
    type CostsTab = 'dashboard' | 'stats' | 'resultados' | 'expenses' | 'products' | 'calculator' | 'settings' | 'materials' | 'fixed' | 'providers' | 'imports' | 'investments' | 'tn_import' | 'conciliacion' | 'cabify' | 'financiera';
    // La pestaña vive en la URL (/costos?tab=resultados): se puede recargar o compartir el link.
    const [activeTab, setActiveTabState] = useState<CostsTab>(() => {
        const t = new URLSearchParams(window.location.search).get('tab') as CostsTab | null;
        return t && COSTS_TABS.some(x => x.key === t) ? t : 'dashboard';
    });
    const setActiveTab = (t: CostsTab) => {
        setActiveTabState(t);
        try {
            const u = new URL(window.location.href);
            if (t === 'dashboard') u.searchParams.delete('tab'); else u.searchParams.set('tab', t);
            window.history.replaceState(window.history.state, '', u.pathname + u.search);
        } catch { /* noop */ }
    };
    const [statsViewMode, setStatsViewMode] = useState<'daily' | 'weekly' | 'monthly'>('daily');
    const [payViewMode, setPayViewMode] = useState<'monto' | 'pedidos'>('monto');
    const [statsMonthFilter, setStatsMonthFilter] = useState<string>('all');
    const [statsRangeFilter, setStatsRangeFilter] = useState<'today' | '7d' | '14d' | 'month' | '30d' | 'all'>('30d');
    const [prodRange, setProdRange] = useState<'today' | '7d' | '14d' | 'month' | '30d' | 'all'>('30d');
    const [prodSort, setProdSort] = useState<'qty' | 'revenue' | 'avg'>('qty');
    const prodSearch = ''; // el buscador vive en el toolbar del DataGrid de Ranking
    const [prodGroupBy, setProdGroupBy] = useState<'parent' | 'variant'>('parent');

    const availableMonths = useMemo(() => {
        const months: Record<string, string> = {};
        orders.forEach(o => {
            const d = new Date(o.created_at);
            const key = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
            if (!months[key]) {
                months[key] = d.toLocaleString('es-AR', { month: 'long', year: 'numeric' });
            }
        });
        return Object.entries(months).sort((a, b) => b[0].localeCompare(a[0]));
    }, [orders]);


    // Extrae el producto padre quitando el bloque de variante "(...)" al final del nombre.
    // Respeta paréntesis anidados, ej "Lampara Aurora (Con dimmer (atenuador), Tornasolado)" → "Lampara Aurora".
    // Los tags entre corchetes [Entrega Inmediata] se conservan (son parte del nombre, no variante).
    const parentProductName = (raw: string): string => {
        let s = (raw || '').trim();
        if (s.endsWith(')')) {
            let depth = 0;
            let cut = -1;
            for (let i = s.length - 1; i >= 0; i--) {
                const ch = s[i];
                if (ch === ')') depth++;
                else if (ch === '(') {
                    depth--;
                    if (depth === 0) { cut = i; break; }
                }
            }
            if (cut > 0) s = s.slice(0, cut).trim();
        }
        return s || (raw || '').trim();
    };

    const productRanking = useMemo(() => {
        const now = new Date();
        const nonCancelled = orders.filter(o => o.status !== 'cancelada' && o.status !== 'presupuesto' && o.status !== 'stock');
        let filtered = nonCancelled;
        if (prodRange === 'today') {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            filtered = nonCancelled.filter(o => new Date(o.created_at) >= start);
        } else if (prodRange === '7d') {
            const c = new Date(now.getTime() - 7 * 86400000);
            filtered = nonCancelled.filter(o => new Date(o.created_at) >= c);
        } else if (prodRange === '14d') {
            const c = new Date(now.getTime() - 14 * 86400000);
            filtered = nonCancelled.filter(o => new Date(o.created_at) >= c);
        } else if (prodRange === 'month') {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            filtered = nonCancelled.filter(o => new Date(o.created_at) >= start);
        } else if (prodRange === '30d') {
            const c = new Date(now.getTime() - 30 * 86400000);
            filtered = nonCancelled.filter(o => new Date(o.created_at) >= c);
        }

        const map = new Map<string, { name: string; qty: number; revenue: number; orders: number }>();
        let totalQty = 0;
        let totalRevenue = 0;
        for (const o of filtered) {
            const prods = Array.isArray((o as any).products) ? (o as any).products : [];
            const seen = new Set<string>();
            for (const p of prods) {
                const rawName = String(p.name || '').trim() || '(sin nombre)';
                const name = prodGroupBy === 'parent' ? parentProductName(rawName) : rawName;
                const qty = Number(p.quantity) || 0;
                const price = Number(p.price) || 0;
                if (qty <= 0) continue;
                const cur = map.get(name) || { name, qty: 0, revenue: 0, orders: 0 };
                cur.qty += qty;
                cur.revenue += price * qty;
                if (!seen.has(name)) { cur.orders += 1; seen.add(name); }
                map.set(name, cur);
                totalQty += qty;
                totalRevenue += price * qty;
            }
        }

        let arr = Array.from(map.values()).map(r => ({
            ...r,
            avgPrice: r.qty > 0 ? r.revenue / r.qty : 0,
        }));
        const q = prodSearch.trim().toLowerCase();
        if (q) arr = arr.filter(r => r.name.toLowerCase().includes(q));
        arr.sort((a, b) => {
            if (prodSort === 'revenue') return b.revenue - a.revenue;
            if (prodSort === 'avg') return b.avgPrice - a.avgPrice;
            return b.qty - a.qty;
        });
        return { rows: arr, totalQty, totalRevenue, distinct: map.size };
    }, [orders, prodRange, prodSort, prodSearch, prodGroupBy]);

    // Proyección del mes en curso: lo facturado hasta ahora extrapolado por run-rate
    // (facturado / fracción del mes transcurrida). Independiente del filtro de rango.
    // Mismo criterio de exclusión que el resto del BI (sin canceladas/presupuestos/stock).
    const monthProjection = useMemo(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const elapsedDays = (now.getTime() - start.getTime()) / 86400000; // con fracción de hoy
        const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        let actual = 0, actualCount = 0, prevMonth = 0;
        for (const o of orders) {
            if (o.status === 'cancelada' || o.status === 'presupuesto' || o.status === 'stock') continue;
            const d = new Date(o.created_at);
            if (d >= start) { actual += Number(o.total_price || 0); actualCount++; }
            else if (d >= prevStart && d < start) prevMonth += Number(o.total_price || 0);
        }
        // Con menos de 2 días de mes la extrapolación es ruido: mostramos solo lo real.
        const projected = elapsedDays >= 2 ? (actual / elapsedDays) * daysInMonth : actual;
        const projectedCount = elapsedDays >= 2 ? Math.round((actualCount / elapsedDays) * daysInMonth) : actualCount;
        return {
            actual, projected, projectedCount, prevMonth,
            pctVsPrev: prevMonth > 0 ? ((projected - prevMonth) / prevMonth) * 100 : null,
            elapsedDays, daysInMonth,
            monthLabel: now.toLocaleString('es-AR', { month: 'long' }),
        };
    }, [orders]);

    const statsData = useMemo(() => {
        const groups: Record<string, { label: string, total: number, shipping: number, bonificado: number, promoDiscount: number, count: number, paid: number, pending: number }> = {};

        const salesByChannel: Record<string, number> = {};
        const countByChannel: Record<string, number> = {};
        const salesByPayment: Record<string, number> = {};
        const countByPayment: Record<string, number> = {};

        // Exclude cancelled orders and budgets from BI stats
        const nonCancelledOrders = orders.filter(o => o.status !== 'cancelada' && o.status !== 'presupuesto' && o.status !== 'stock');

        // Apply range filter first
        const now = new Date();
        let rangeFiltered = nonCancelledOrders;
        let prevFiltered: any[] = [];
        let rangeDays = 0;

        if (statsRangeFilter === 'today') {
            rangeDays = 1;
            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
            rangeFiltered = nonCancelledOrders.filter(o => new Date(o.created_at) >= startOfToday);
            prevFiltered = nonCancelledOrders.filter(o => { const d = new Date(o.created_at); return d >= startOfYesterday && d < startOfToday; });
        } else if (statsRangeFilter === '7d') {
            rangeDays = 7;
            const cutoff = new Date(now.getTime() - 7 * 86400000);
            const prevCutoff = new Date(now.getTime() - 14 * 86400000);
            rangeFiltered = nonCancelledOrders.filter(o => new Date(o.created_at) >= cutoff);
            prevFiltered = nonCancelledOrders.filter(o => { const d = new Date(o.created_at); return d >= prevCutoff && d < cutoff; });
        } else if (statsRangeFilter === '14d') {
            rangeDays = 14;
            const cutoff = new Date(now.getTime() - 14 * 86400000);
            const prevCutoff = new Date(now.getTime() - 28 * 86400000);
            rangeFiltered = nonCancelledOrders.filter(o => new Date(o.created_at) >= cutoff);
            prevFiltered = nonCancelledOrders.filter(o => { const d = new Date(o.created_at); return d >= prevCutoff && d < cutoff; });
        } else if (statsRangeFilter === 'month') {
            rangeDays = 30;
            rangeFiltered = nonCancelledOrders.filter(o => {
                const d = new Date(o.created_at);
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
            });
            // Mes anterior HASTA el mismo día y hora (comparación justa: el mes actual
            // está en curso — contra el mes anterior completo el % daba siempre en contra).
            // Si hoy es 31 y el mes pasado tenía 30, corta en su último día.
            const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const daysInPrev = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
            const sameDayCut = new Date(now.getFullYear(), now.getMonth() - 1, Math.min(now.getDate(), daysInPrev), now.getHours(), now.getMinutes(), now.getSeconds());
            prevFiltered = nonCancelledOrders.filter(o => {
                const d = new Date(o.created_at);
                return d.getMonth() === prevMonth.getMonth() && d.getFullYear() === prevMonth.getFullYear() && d <= sameDayCut;
            });
        } else if (statsRangeFilter === '30d') {
            rangeDays = 30;
            const cutoff = new Date(now.getTime() - 30 * 86400000);
            const prevCutoff = new Date(now.getTime() - 60 * 86400000);
            rangeFiltered = nonCancelledOrders.filter(o => new Date(o.created_at) >= cutoff);
            prevFiltered = nonCancelledOrders.filter(o => { const d = new Date(o.created_at); return d >= prevCutoff && d < cutoff; });
        }

        // Compute previous period totals
        const prevTotals = {
            total: prevFiltered.reduce((s, o) => s + Number(o.total_price || 0), 0),
            paid: prevFiltered.reduce((s, o) => s + ((o.payment_status === 'paid' || o.status === 'entregado') ? Number(o.total_price || 0) : 0), 0),
            pending: prevFiltered.reduce((s, o) => s + ((o.payment_status !== 'paid' && o.status !== 'entregado') ? Number(o.total_price || 0) : 0), 0),
            shipping: prevFiltered.reduce((s, o) => s + Number(o.shipping_cost_customer || 0), 0),
            bonificado: prevFiltered.reduce((s, o) => s + (Number(o.shipping_cost_customer || 0) === 0 ? Number(o.shipping_cost_owner || 0) : 0), 0),
            promoDiscount: prevFiltered.reduce((s, o) => s + Number(o.promo_discount || 0), 0),
            count: prevFiltered.length,
        };
        prevTotals.total = prevTotals.total || 0;

        // Then apply month filter
        const filteredOrders = statsMonthFilter === 'all'
            ? rangeFiltered
            : rangeFiltered.filter(o => {
                const d = new Date(o.created_at);
                const key = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}`;
                return key === statsMonthFilter;
            });

        filteredOrders.forEach(order => {
            const date = new Date(order.created_at);
            let key = '';
            let label = '';

            if (statsViewMode === 'daily') {
                key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
                label = date.toLocaleString('es-AR', { day: 'numeric', month: 'short' });
            } else if (statsViewMode === 'monthly') {
                key = `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}`;
                label = date.toLocaleString('es-AR', { month: 'short', year: 'numeric' });
            } else {
                // Weekly
                const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
                const pastDaysOfYear = (date.getTime() - firstDayOfYear.getTime()) / 86400000;
                const weekNum = Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
                key = `${date.getFullYear()}-W${weekNum.toString().padStart(2, '0')}`;
                label = `Sem ${weekNum}`;
            }

            if (!groups[key]) groups[key] = { label, total: 0, shipping: 0, bonificado: 0, promoDiscount: 0, count: 0, paid: 0, pending: 0 };
            const amount = Number(order.total_price || 0);
            groups[key].total += amount;
            groups[key].shipping += Number(order.shipping_cost_customer || 0);
            groups[key].bonificado += (Number(order.shipping_cost_customer || 0) === 0 ? Number(order.shipping_cost_owner || 0) : 0);
            groups[key].promoDiscount += Number(order.promo_discount || 0);
            groups[key].count += 1;

            if (order.payment_status === 'paid' || order.status === 'entregado') {
                groups[key].paid += amount;
            } else {
                groups[key].pending += amount;
            }

            // Pie Chart Data
            const source = order.source || 'tiendanube';
            if (!salesByChannel[source]) salesByChannel[source] = 0;
            salesByChannel[source] += amount;
            if (!countByChannel[source]) countByChannel[source] = 0;
            countByChannel[source] += 1;

            // Pie de medios de pago
            const bucket = getPaymentBucket(order);
            salesByPayment[bucket] = (salesByPayment[bucket] || 0) + amount;
            countByPayment[bucket] = (countByPayment[bucket] || 0) + 1;
        });

        const timeline = Object.entries(groups)
            .map(([key, data]) => ({ key, ...data }))
            .sort((a, b) => a.key.localeCompare(b.key));

        const totalPieValue = Object.values(salesByChannel).reduce((s, v) => s + v, 0);
        const pieData = Object.entries(salesByChannel)
            .map(([name, value]) => ({
                name,
                value,
                count: countByChannel[name] || 0,
                percent: totalPieValue > 0 ? ((value / totalPieValue) * 100).toFixed(1) : '0'
            }))
            .sort((a, b) => b.value - a.value);

        const totalPaymentValue = Object.values(salesByPayment).reduce((s, v) => s + v, 0);
        const totalPaymentCount = Object.values(countByPayment).reduce((s, v) => s + v, 0);
        const paymentData = Object.entries(salesByPayment)
            .map(([name, value]) => ({
                name,
                value,
                count: countByPayment[name] || 0,
                percent: totalPaymentValue > 0 ? ((value / totalPaymentValue) * 100).toFixed(1) : '0',
                countPercent: totalPaymentCount > 0 ? (((countByPayment[name] || 0) / totalPaymentCount) * 100).toFixed(1) : '0'
            }))
            .sort((a, b) => b.value - a.value);

        const pendingOrders = filteredOrders
            .filter(o => o.payment_status !== 'paid' && o.status !== 'entregado')
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        return { timeline, pieData, paymentData, filteredCount: filteredOrders.length, prevTotals, hasPrev: prevFiltered.length > 0, pendingOrders };
    }, [orders, statsViewMode, statsMonthFilter, statsRangeFilter]);
    const [settings, setSettings] = useState<CostSettings | null>(null);
    const [showPromos, setShowPromos] = useState(false);
    const [materials, setMaterials] = useState<Material[]>([]);
    const [fixedCosts, setFixedCosts] = useState<FixedCost[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [costHistory, setCostHistory] = useState<{ entity: string; ref_id: number; data: any; valid_from: string }[]>([]);
    const [loading, setLoading] = useState(false);
    const [saveMsg, setSaveMsg] = useState('');

    // Calculator State
    const [productName, setProductName] = useState('');
    const [selectedMaterials, setSelectedMaterials] = useState<{ id: number; quantity: number; width_mm?: number; height_mm?: number }[]>([]);
    const [markup, setMarkup] = useState(30);
    const [calculationResult, setCalculationResult] = useState<any>(null);

    // Fixed costs
    const [newFixedName, setNewFixedName] = useState('');
    const [newFixedAmount, setNewFixedAmount] = useState('');
    const [showNewFixedForm, setShowNewFixedForm] = useState(false);

    // Expenses
    const [expenses, setExpenses] = useState<Expense[]>([]);
    // Venta mensual de la Suite por mes 'YYYY-MM' (carga manual; 10% comisión + 3% transf., sin IVA/IIBB)
    const [suiteSales, setSuiteSales] = useState<Record<string, number>>({});
    const [suiteDraft, setSuiteDraft] = useState<string>('');
    const [providers, setProviders] = useState<Provider[]>([]);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
    const [expenseForm, setExpenseForm] = useState({ amount: '', provider_id: '' as string, payment_method: 'efectivo', notes: '', category: 'otros', expense_date: new Date().toISOString().slice(0, 10) });
    const [newProviderName, setNewProviderName] = useState('');
    const [showNewProvider, setShowNewProvider] = useState(false);
    const [expenseMonthFilter, setExpenseMonthFilter] = useState<string>(() => {
        const now = new Date();
        return `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}`;
    });

    const calcCost = useCallback((variantMats: TnVariantMaterial[], productMats: TnProductMaterial[] = []) => {
        if (!settings) return { cost: 0 };
        let totalCost = 0;
        const allMats = [...variantMats, ...productMats];
        allMats.forEach(item => {
            const mat = materials.find(m => m.id === item.material_id);
            if (!mat) return;
            if (mat.type === 'simple') {
                let unitCost = mat.unit_cost;
                if (mat.currency === 'USD') unitCost *= settings.usd_rate;
                totalCost += unitCost * item.quantity;
            } else if (mat.type === 'acrylic') {
                const sheetArea = (mat.width_mm || 1220) * (mat.height_mm || 2440);
                const sheetWeight = mat.weight_kg || 11.4;
                let ppk = mat.unit_cost;
                if (mat.currency === 'USD') ppk *= settings.usd_rate;
                const pps = ppk * sheetWeight * (1 + settings.iva_percent / 100);
                const cpm = pps / sheetArea;
                const ra = (item.width_mm || 0) * (item.height_mm || 0);
                const aw = ra * (1 + settings.acrylic_waste_percent / 100);
                totalCost += cpm * aw * item.quantity;
            }
        });
        return { cost: totalCost };
    }, [settings, materials]);

    // ═══════════ COSTO DE LO VENDIDO CON VERSIONES (10/10/2026) ═══════════
    // Cada venta se costea con los valores VIGENTES EL DÍA DE LA VENTA: precio de cada insumo,
    // dólar/euro/IVA/desperdicio, costo directo y recetas (tabla cost_history, la llenan triggers;
    // migr 103). Subir un insumo hoy NO cambia los meses ya vendidos.
    // Si a la fecha de la venta no había ninguna versión (ej. el costo se cargó por primera vez
    // después), se usa la PRIMERA conocida: completar un costo faltante sí corrige el pasado;
    // cambiar uno existente, no.
    const histIndex = useMemo(() => {
        const m = new Map<string, { t: number; data: any }[]>();
        for (const h of costHistory) {
            const k = `${h.entity}:${h.ref_id}`;
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push({ t: new Date(h.valid_from).getTime(), data: h.data });
        }
        m.forEach(arr => arr.sort((a, b) => a.t - b.t));
        const epochs = Array.from(new Set(costHistory.map(h => new Date(h.valid_from).getTime()))).sort((a, b) => a - b);
        return { m, epochs };
    }, [costHistory]);

    // versión vigente a ts: undefined = esa cosa no tiene historial (se usa el valor actual);
    // null = no había versión todavía (y fallback=false)
    const versionAt = useCallback((entity: string, id: number | string, ts: number, fallback: boolean): any | null | undefined => {
        const arr = histIndex.m.get(`${entity}:${id}`);
        if (!arr || !arr.length) return undefined;
        let hit: { t: number; data: any } | null = null;
        for (const v of arr) { if (v.t <= ts) hit = v; else break; }
        return hit ? hit.data : (fallback ? arr[0].data : null);
    }, [histIndex]);

    // calcCost puro (mismas fórmulas que calcCost, con insumos y settings de una fecha)
    const calcCostWith = (mats: any[], pmats: any[], matById: Map<number, Material>, st: CostSettings): number => {
        let total = 0;
        for (const item of [...mats, ...pmats]) {
            const mat = matById.get(Number(item.material_id));
            if (!mat) continue;
            if (mat.type === 'simple') {
                let unitCost = Number(mat.unit_cost);
                if (mat.currency === 'USD') unitCost *= st.usd_rate;
                total += unitCost * Number(item.quantity);
            } else if (mat.type === 'acrylic') {
                const sheetArea = (mat.width_mm || 1220) * (mat.height_mm || 2440);
                const sheetWeight = mat.weight_kg || 11.4;
                let ppk = Number(mat.unit_cost);
                if (mat.currency === 'USD') ppk *= st.usd_rate;
                const pps = ppk * sheetWeight * (1 + st.iva_percent / 100);
                const cpm = pps / sheetArea;
                const ra = (item.width_mm || 0) * (item.height_mm || 0);
                const aw = ra * (1 + st.acrylic_waste_percent / 100);
                total += cpm * aw * Number(item.quantity);
            }
        }
        return total;
    };

    // Índice de costos (variant_id / SKU / producto / nombre) armado con el estado de una fecha.
    // Nombre para cruzar ventas con el catálogo (22/10/2026): sin tildes, sin etiquetas [LANZAMIENTO]/
    // [EXCLUSIVO]/[NUEVA]… y sin puntuación. Las ventas viejas sin IDs de TN guardaron el nombre de
    // ese momento ("[LANZAMIENTO] Lampara Sun&Moon") y el catálogo hoy dice "Lámpara Sun&Moon":
    // no cruzaban y el CMV asumía 50% (septiembre: confianza 65% → 84% con esto).
    const matchName = (s: string) => String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/\[[^\]]*\]/g, ' ').replace(/[^a-z0-9&]+/g, ' ').trim();

    const buildItemCost = (ts: number, fallback: boolean): ((it: any) => number | null) => {
        const st: CostSettings = { ...(settings as CostSettings), ...(versionAt('settings', settings!.id, ts, true) || {}) };
        const matById = new Map<number, Material>();
        for (const m of materials) matById.set(m.id, { ...m, ...(versionAt('material', m.id, ts, true) || {}) });
        // costo directo: sin historial → actual; sin versión a la fecha → no tenía
        const directAt = (entity: string, obj: any) => {
            const v = versionAt(entity, obj.id, ts, fallback);
            return v === undefined ? obj : (v || { direct_cost: null, direct_cost_currency: null });
        };
        const recipeAt = (entity: string, id: number, current: any[]) => {
            const v = versionAt(entity, id, ts, fallback);
            return v === undefined ? current : (v || []);
        };
        const productsAt = products.map((pr: any) => ({
            ...pr, ...directAt('product_direct', pr),
            materials: recipeAt('product_recipe', pr.id, pr.materials || []),
            variants: (pr.variants || []).map((v: any) => ({
                ...v, ...directAt('variant_direct', v),
                materials: recipeAt('variant_recipe', v.id, v.materials || []),
            })),
        }));
        // índice por producto (base del nombre) con el costo de cada variante costeada
        const prodByBase = new Map<string, { id: number; variants: { cost: number; vals: string[] }[] }>();
        const costByVariantId = new Map<number, number>();
        const costByProductId = new Map<number, number>();
        const costBySku = new Map<string, number>();
        for (const pr of productsAt) {
            const entry = { id: pr.id, variants: [] as { cost: number; vals: string[] }[] };
            // costo directo (reventa): variante > producto > insumos (USD → usd_rate)
            const dcOf = (obj: any): number => {
                const val = Number(obj?.direct_cost) || 0;
                if (val <= 0) return 0;
                const cur = obj?.direct_cost_currency;
                return cur === 'USD' ? val * (st?.usd_rate || 0)
                    : cur === 'EUR' ? val * (st?.eur_rate || 1800)
                    : val;
            };
            const prodDirect = dcOf(pr);
            for (const v of (pr.variants || [])) {
                const direct = dcOf(v) || prodDirect;
                let c = 0;
                if (direct > 0) {
                    c = direct;
                } else {
                    const mats = (v as any).materials || [];
                    const pmats = (pr as any).materials || [];
                    if (!mats.length && !pmats.length) continue;
                    c = calcCostWith(mats, pmats, matById, st);
                }
                if (c <= 0) continue;
                let vals: string[] = [];
                let raw: any = (v as any).values;
                if (typeof raw === 'string') { try { raw = JSON.parse(raw); } catch { raw = []; } }
                if (Array.isArray(raw)) vals = raw.filter((x: any) => typeof x === 'string');
                entry.variants.push({ cost: c, vals });
                // índices robustos, independientes del nombre (que puede renombrarse):
                if ((v as any).id) costByVariantId.set(Number((v as any).id), c);
                const sku = String((v as any).sku || '').trim().toLowerCase();
                if (sku) costBySku.set(sku, c);
            }
            prodByBase.set(matchName(pr.name), entry);
            if (entry.variants.length) costByProductId.set(Number(pr.id), entry.variants.reduce((s2, vv) => s2 + vv.cost, 0) / entry.variants.length);
        }

        // Prioridad de matching: variant_id de TN → SKU → product_id → nombre exacto → prefijo.
        // Los IDs no dependen del nombre (que se renombra en TN y rompía el match).
        const itemCost = (it: any): number | null => {
            const vid = Number(it?.tn_variant_id) || 0;
            if (vid && costByVariantId.has(vid)) return costByVariantId.get(vid)!;
            const sku = String(it?.sku || '').trim().toLowerCase();
            if (sku && costBySku.has(sku)) return costBySku.get(sku)!;
            const pid = Number(it?.tn_product_id) || 0;
            if (pid && costByProductId.has(pid)) return costByProductId.get(pid)!;
            const name = String(it?.name || '');
            // "Producto (A, B)" o "Producto – A / B" (formato de las ventas manuales)
            const base = matchName(name.split(' (')[0].split(' – ')[0]);
            let entry = prodByBase.get(base);
            // Fallback por PREFIJO: ítems vendidos con un nombre que ya no existe tal cual
            // en el catálogo (ej "Parlante ... - Blanco" cuando el catálogo tiene
            // "Parlante ..." o "... - Negro" renombrado). Toma el producto costeado cuyo
            // nombre sea prefijo del ítem (el más largo), o al revés.
            if (!entry || !entry.variants.length) {
                let bestKey = '';
                prodByBase.forEach((v, k) => {
                    if (!v.variants.length) return;
                    if ((base.startsWith(k) || k.startsWith(base)) && k.length > bestKey.length) bestKey = k;
                });
                if (bestKey) entry = prodByBase.get(bestKey);
            }
            if (!entry || !entry.variants.length) return null;
            const paren = (name.match(/\((.+)\)\s*$/)?.[1] || name.split(' – ')[1] || '').toLowerCase();
            if (paren) {
                const hit = entry.variants.find(vv => vv.vals.length && vv.vals.every(x => paren.includes(x.toLowerCase())));
                if (hit) return hit.cost;
            }
            // sin match exacto: promedio de las variantes costeadas del producto
            return entry.variants.reduce((s2, vv) => s2 + vv.cost, 0) / entry.variants.length;
        };

        return itemCost;
    };

    // itemCost(ítem, fecha de la venta), con caché por "época" (entre dos cambios de costos)
    const itemCostAt = useMemo(() => {
        if (!settings) return (_it: any, _ts: number): number | null => null;
        const cache = new Map<string, (it: any) => number | null>();
        const ep = histIndex.epochs;
        return (it: any, ts: number): number | null => {
            let lo = 0, hi = ep.length - 1, e = -1;
            while (lo <= hi) { const mid = (lo + hi) >> 1; if (ep[mid] <= ts) { e = mid; lo = mid + 1; } else hi = mid - 1; }
            const at = e >= 0 ? ep[e] : -Infinity;
            const get = (fb: boolean) => {
                const k = `${e}|${fb ? 1 : 0}`;
                if (!cache.has(k)) cache.set(k, buildItemCost(at, fb));
                return cache.get(k)!;
            };
            return get(false)(it) ?? get(true)(it);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [histIndex, products, materials, settings, versionAt]);

    // ═══════════ ESTADO DE RESULTADOS (pestaña Resultados) ═══════════
    // P&L mensual "de acá para adelante": ingresos netos de IVA → CMV (costo de
    // materiales de lo vendido, con % de cobertura) → variables por medio de pago
    // (comisiones + IIBB) + envíos a cargo nuestro → margen de contribución →
    // fijos (cost_fixed, mensual recurrente; Meta ads cuenta como marketing) y
    // gastos del mes (expenses por categoría) → resultado operativo.
    const [simRevenue, setSimRevenue] = useState<string>('');
    const [resMonth, setResMonth] = useState<string>(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    });

    // Pauta REAL (Meta Ads + Google Ads) por día, de adhub vía sync-ad-spend → tabla ad_spend
    // (20/10/2026). Reemplaza el "Meta ads" fijo de cost_fixed en los meses que tienen datos.
    // Solo Krilic (marca_id 1); monto_ars ya convertido con el dólar oficial de cada día.
    // Costo REAL de Cabify por pedido (cabify_deliveries.estimate, centavos) y envíos sueltos sin pedido
    // por mes (22/10/2026). Antes Resultados usaba shipping_cost_owner de TN, que para logística
    // propia es la tarifa que ve el cliente y para "Envío gratis" es 0: el costo real no entraba.
    const [cabifyCost, setCabifyCost] = useState<{ byOrder: Map<number, number>; looseByMonth: Map<string, number> }>({ byOrder: new Map(), looseByMonth: new Map() });
    useEffect(() => {
        (async () => {
            const rows: any[] = [];
            for (let from = 0; ; from += 1000) {
                const { data, error } = await supabase.from('cabify_deliveries').select('order_id, status, estimate, created_at')
                    .not('status', 'in', '(deleted,cancel,cancelled,pickupfailed,created,ready)').order('id').range(from, from + 999);
                if (error || !data) break;
                rows.push(...data);
                if (data.length < 1000) break;
            }
            const byOrder = new Map<number, number>(), looseByMonth = new Map<string, number>();
            for (const r of rows) {
                const ars = (Number(r.estimate?.amount) || 0) / 100;
                if (!ars) continue;
                if (r.order_id) byOrder.set(Number(r.order_id), (byOrder.get(Number(r.order_id)) || 0) + ars);
                else { const k = String(r.created_at).slice(0, 7); looseByMonth.set(k, (looseByMonth.get(k) || 0) + ars); }
            }
            setCabifyCost({ byOrder, looseByMonth });
        })();
    }, []);
    const [adSpend, setAdSpend] = useState<Map<string, { meta: number; google: number; lastDay: number }>>(new Map());
    useEffect(() => {
        (async () => {
            const rows: any[] = [];
            for (let from = 0; ; from += 1000) {
                const { data, error } = await supabase.from('ad_spend').select('fecha, plataforma, monto_ars')
                    .eq('marca_id', 1).order('fecha').range(from, from + 999);
                if (error || !data) break;
                rows.push(...data);
                if (data.length < 1000) break;
            }
            const m = new Map<string, { meta: number; google: number; lastDay: number }>();
            for (const r of rows) {
                const k = String(r.fecha).slice(0, 7);
                const e = m.get(k) || { meta: 0, google: 0, lastDay: 0 };
                if (r.plataforma === 'google_ads') e.google += Number(r.monto_ars) || 0; else e.meta += Number(r.monto_ars) || 0;
                e.lastDay = Math.max(e.lastDay, Number(String(r.fecha).slice(8, 10)));
                m.set(k, e);
            }
            setAdSpend(m);
        })();
    }, []);

    // WhatsApp (Meta) del mes: costo variable (20/10/2026). Mensajes enviados de whatsapp_logs con
    // las tarifas de src/WaUsage.tsx, en USD → a pesos con el dólar de la configuración.
    const [waUsage, setWaUsage] = useState<Record<string, WaMonthUsage>>({});
    useEffect(() => {
        if (waUsage[resMonth]) return;
        let alive = true;
        fetchWaMonthUsage(resMonth).then(u => { if (alive) setWaUsage(w => ({ ...w, [resMonth]: u })); }).catch(() => {});
        return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resMonth]);

    const resultados = useMemo(() => {
        const [ry, rm] = resMonth.split('-').map(Number);
        const start = new Date(ry, rm - 1, 1);
        const end = new Date(ry, rm, 1);

        const mOrders = orders.filter((o: any) => {
            if (o.status === 'cancelada' || o.status === 'presupuesto' || o.status === 'stock') return false;
            const d = new Date(o.created_at);
            return d >= start && d < end;
        });

        let revenueGross = 0, revenueNet = 0, cmv = 0, coveredNet = 0, itemsNetTotal = 0, fees = 0, shippingOwn = 0, shippingCabify = 0;
        let feesCard = 0, feesClean = 0, feesTN = 0, feesIIBB = 0, feesML = 0;
        const byProduct = new Map<string, { name: string; units: number; net: number; cost: number; covered: boolean; orderIds: string[] }>();
        for (const o of mOrders) {
            const bucket = getPaymentBucket(o);
            const gross = Number(o.total_price || 0);
            // IVA: SOLO las ventas con tarjeta/MP se facturan (pagan IVA).
            // Transferencia y efectivo no llevan IVA.
            // MercadoLibre (22/10/2026): se factura con IVA como tarjeta y cobra 15% de plataforma
            // (incluye el cobro: no lleva además la comisión de tarjeta).
            const esML = o.source === 'mercadolibre';
            const netFactor = (esML || bucket === 'tarjeta' || bucket === 'billetera') ? 1 / 1.21 : 1;
            const net = gross * netFactor;
            revenueGross += gross;
            revenueNet += net;
            // envío: costo real de Cabify si salió por Cabify; si no, lo que informa TN (Andreani/punto de retiro)
            const cab = cabifyCost.byOrder.get(Number(o.id));
            if (cab != null) shippingCabify += cab; else shippingOwn += Number(o.shipping_cost_owner || 0);
            // Gastos de venta reales (definidos 24/08): IIBB = 0 por ahora.
            //  · Pago Nube / MercadoPago / tarjeta manual: 3,5% + IVA en 1 pago;
            //    en cuotas se suma 10,5% + IVA (payment_details.installments).
            //  · Transferencia: 3% Clean Managers por el cobro + 1% TN si es orden de Tiendanube.
            //  · Efectivo: solo el 1% de TN (ventas manuales en efectivo: sin fee).
            const esTN = o.source === 'tiendanube';
            const cuotas = Number((o as any).payment_details?.installments) || 1;
            if (esML) {
                feesML += gross * 0.15;
            } else if (bucket === 'tarjeta' || bucket === 'billetera') {
                feesCard += gross * (0.035 + (cuotas > 1 ? 0.105 : 0)) * 1.21;
            } else if (bucket === 'transferencia') {
                feesClean += gross * 0.03;
                if (esTN) feesTN += gross * 0.01;
            } else {
                if (esTN) feesTN += gross * 0.01; // efectivo
            }
            const items: any[] = Array.isArray(o.products) ? o.products : [];
            const itemsGross = items.reduce((s2, it) => s2 + Number(it.price || 0) * (Number(it.quantity) || 1), 0) || gross;
            for (const it of items) {
                const qty = Number(it.quantity) || 1;
                const itGross = Number(it.price || 0) * qty;
                // el neto del item hereda la proporción del total real de la orden (descuentos incl.)
                const itNet = itemsGross > 0 ? (itGross / itemsGross) * net : 0;
                itemsNetTotal += itNet;
                // mismo producto aunque se haya vendido con otro nombre ("[LANZAMIENTO] Lampara X" = "Lámpara X")
                const base = String(it.name || '').split(' (')[0].split(' – ')[0].replace(/\[[^\]]*\]/g, ' ').replace(/\s+/g, ' ').trim();
                const c = itemCostAt(it, new Date(o.created_at).getTime());
                const key = matchName(base);
                const agg = byProduct.get(key) || { name: base, units: 0, net: 0, cost: 0, covered: c != null, orderIds: [] as string[] };
                agg.units += qty; agg.net += itNet;
                const oid = '#' + getOrderDisplayId(o);
                if (!agg.orderIds.includes(oid)) agg.orderIds.push(oid);
                if (c != null) {
                    agg.cost += c * qty; cmv += c * qty; coveredNet += itNet;
                } else {
                    // sin costo cargado: DEFAULT 50% del valor de venta hasta cargar el real.
                    // No suma a coveredNet: el medidor de confianza refleja solo costo REAL.
                    const est = itGross * 0.5;
                    agg.cost += est; cmv += est;
                    agg.covered = false;
                }
                byProduct.set(key, agg);
            }
        }

        // ── Cuánto del mes ya pasó (lo usan el prorrateo de marketing y el forecast) ──
        const nowD = new Date();
        const isCurrentMonth = nowD >= start && nowD < end;
        const daysInMonth = new Date(ry, rm, 0).getDate();
        const elapsedFrac = isCurrentMonth
            ? Math.min(1, Math.max(0.03, (nowD.getTime() - start.getTime()) / (daysInMonth * 86400000)))
            : 1;
        // Días ya corridos del mes (1..daysInMonth). En un mes cerrado, todos.
        const elapsedDaysMonth = isCurrentMonth ? Math.min(daysInMonth, nowD.getDate()) : daysInMonth;

        // ── Costos fijos ──
        // MARKETING SE PRORRATEA POR DÍA (pedido 01/10). La pauta se gasta casi lineal a lo
        // largo del mes: cargar los $4,5M enteros el día 1 hacía que el mes en curso
        // arrancara con un resultado falsamente catastrófico y se fuera "arreglando" solo.
        // Ahora se imputa monto_mensual / días_del_mes × días_corridos.
        // Los demás fijos (alquiler, sueldos) siguen enteros: son obligaciones del mes,
        // no un gasto que se acumule día a día.
        // Costos fijos VIGENTES EN ESE MES (cost_history 'fixed', migr 112): la foto al cierre del mes;
        // mes en curso o sin historial → la lista de hoy. Subir un sueldo ya no cambia meses pasados.
        const fixedAt = isCurrentMonth ? null : versionAt('fixed', 0, end.getTime() - 1, true);
        const fixedOfMonth: any[] = Array.isArray(fixedAt) ? fixedAt : fixedCosts;
        const fixedCostsAll = fixedOfMonth.reduce((s2: number, f: any) => s2 + Number(f.amount_ars || 0), 0);
        const marketingFixed = fixedOfMonth.filter((f: any) => /ads|marketing|pauta|meta/i.test(f.name || ''))
            .reduce((s2: number, f: any) => s2 + Number(f.amount_ars || 0), 0);
        const fixedSinMkt = fixedCostsAll - marketingFixed;
        // Pauta real (ad_spend) si el mes tiene datos: lo gastado hasta el último día cargado
        // (adhub trae hasta AYER); para la proyección/break-even, el ritmo diario × días del mes.
        // Sin datos (meses viejos o si el sync falla): como antes, fijo mensual prorrateado.
        const ads = adSpend.get(resMonth);
        const adsDays = ads ? (isCurrentMonth ? Math.max(1, Math.min(ads.lastDay, daysInMonth)) : daysInMonth) : 0;
        const marketingSource: 'ads' | 'fijo' = ads ? 'ads' : 'fijo';
        const adsMeta = ads?.meta || 0, adsGoogle = ads?.google || 0;
        const marketing = ads ? adsMeta + adsGoogle : (daysInMonth > 0 ? marketingFixed / daysInMonth : 0) * elapsedDaysMonth;
        const marketingDaily = ads ? marketing / adsDays : (daysInMonth > 0 ? marketingFixed / daysInMonth : 0);
        const marketingFull = ads ? (isCurrentMonth ? marketingDaily * daysInMonth : marketing) : marketingFixed;
        const fixedAll = fixedSinMkt + marketingFull;
        const adsThroughDay = ads ? adsDays : 0;
        // CANJES (22/10/2026): lo regalado a creadores es marketing. Costo de los productos con el
        // costo vigente el día del canje; sin costo cargado, 50% del precio de lista (como el CMV).
        let canjeCost = 0, canjeCount = 0, canjeList = 0;
        for (const o of canjeOrders) {
            if (o.status === 'cancelada') continue;
            const d = new Date(o.created_at);
            if (d < start || d >= end) continue;
            canjeCount++;
            for (const it of (Array.isArray(o.products) ? o.products : [])) {
                const qty = Number(it.quantity) || 1;
                const list = Number(it.price || 0) * qty;
                canjeList += list;
                const c = itemCostAt(it, d.getTime());
                canjeCost += c != null ? c * qty : list * 0.5;
            }
        }
        const canjeFull = isCurrentMonth ? canjeCost / elapsedFrac : canjeCost;
        const fixedTotal = fixedSinMkt + marketing + canjeCost;

        const mExpenses = expenses.filter((e: any) => {
            const d = (e as any).expense_date ? new Date((e as any).expense_date + 'T12:00:00') : new Date(e.created_at);
            return d >= start && d < end;
        });
        const expByCat = new Map<string, number>();
        for (const e of mExpenses) {
            const cat = ((e as any).category || 'otros');
            expByCat.set(cat, (expByCat.get(cat) || 0) + Number(e.amount || 0));
        }
        const expensesTotal = Array.from(expByCat.values()).reduce((a, b) => a + b, 0);

        // ── Venta Suite del mes (carga manual): 10% comisión + 3% por la transferencia.
        // Sin IVA ni IIBB (tratamiento efectivo). CMV: default 50% (sin detalle de ítems),
        // cuenta como NO cubierto en el medidor de confianza.
        const suiteGross = Number(suiteSales[resMonth]) || 0;
        const feesSuite = suiteGross * 0.10 + suiteGross * 0.03;
        const suiteCmv = suiteGross * 0.5;
        revenueGross += suiteGross;
        revenueNet += suiteGross;          // sin IVA
        cmv += suiteCmv;
        itemsNetTotal += suiteGross;       // baja la confianza: no hay costo real de la Suite

        fees = feesCard + feesClean + feesTN + feesIIBB + feesSuite + feesML;
        const grossMargin = revenueNet - cmv;
        const wa = waUsage[resMonth];
        const waCost = wa ? wa.costUSD * (Number(settings?.usd_rate) || 0) : 0;
        shippingCabify += cabifyCost.looseByMonth.get(resMonth) || 0; // envíos cargados a mano en /cabify, sin pedido
        const contribution = grossMargin - fees - shippingOwn - shippingCabify - waCost;
        // GASTOS (tabla expenses) EXCLUIDOS del resultado por ahora (pedido 25/08:
        // la carga histórica no es confiable). Solo fijos. Reincorporar cuando se ordenen.
        const result = contribution - fixedTotal;
        const coverage = itemsNetTotal > 0 ? (coveredNet / itemsNetTotal) * 100 : 0;

        // ── Simulador: ratios del mes para break-even, forecast y calculadora ──
        // Margen de contribución como % de la FACTURACIÓN bruta (mezcla real del mes:
        // medios de pago, IVA solo tarjeta, CMV con defaults del 50%).
        const cmRatio = revenueGross > 0 ? contribution / revenueGross : 0;
        // ── Forecast del mes en curso ──
        // El prorrateo lineal (facturado / fracción del mes) ignora dos patrones reales:
        // (a) el arranque de mes no vende igual que el final (efecto sueldo), y
        // (b) el mix de días de semana que le queda al mes no es el que ya pasó.
        // Dos métodos con historia real, promediados:
        //  A. Curva histórica: qué % de la facturación suele estar hecho al día D
        //     (últimos 3 meses completos) → proyección = MTD / share_esperado.
        //  B. Ritmo semanal: promedio facturado por día de semana (últimas 8 semanas)
        //     aplicado a los días que faltan → proyección = MTD + lo esperado restante.
        // Fallback: lineal, si no hay historia suficiente.
        // la Suite es un monto mensual fijo: no se prorratea por días (solo la parte de órdenes)
        const ordersGross = revenueGross - suiteGross;
        const projLinear = ordersGross / elapsedFrac + suiteGross;
        let projCurve: number | null = null, projWeekly: number | null = null;
        if (isCurrentMonth) {
            const valid = orders.filter((o: any) => o.status !== 'cancelada' && o.status !== 'presupuesto' && o.status !== 'stock');
            const revByDay = new Map<string, number>();
            let minT = Infinity;
            for (const o of valid) {
                const d = new Date(o.created_at);
                if (d.getTime() < minT) minT = d.getTime();
                const k = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
                revByDay.set(k, (revByDay.get(k) || 0) + Number(o.total_price || 0));
            }
            const today = nowD.getDate();
            const fracToday = Math.max(0.05, (nowD.getHours() * 60 + nowD.getMinutes()) / 1440);

            // A) curva por día del mes (hasta 3 meses completos con data completa)
            const shares: number[] = [];
            for (let back = 1; back <= 5 && shares.length < 3; back++) {
                const mStart = new Date(ry, rm - 1 - back, 1);
                if (mStart.getTime() < minT) break; // la data no cubre ese mes entero
                const dInM = new Date(ry, rm - back, 0).getDate();
                let total = 0, cum = 0;
                for (let day = 1; day <= dInM; day++) {
                    const rev = revByDay.get(`${mStart.getFullYear()}-${mStart.getMonth()}-${day}`) || 0;
                    total += rev;
                    if (day < Math.min(today, dInM)) cum += rev;
                    else if (day === Math.min(today, dInM)) cum += rev * fracToday;
                }
                if (total > 0) shares.push(cum / total);
            }
            if (shares.length) {
                const share = shares.reduce((a, b) => a + b, 0) / shares.length;
                if (share >= 0.04) projCurve = ordersGross / share + suiteGross;
            }

            // B) ritmo por día de semana (últimas 8 semanas cerradas ayer)
            const wdSum = [0, 0, 0, 0, 0, 0, 0], wdN = [0, 0, 0, 0, 0, 0, 0];
            let daysWithData = 0;
            for (let i = 1; i <= 56; i++) {
                const d = new Date(nowD.getFullYear(), nowD.getMonth(), nowD.getDate() - i);
                if (d.getTime() < minT) break;
                const rev = revByDay.get(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`) || 0;
                wdSum[d.getDay()] += rev; wdN[d.getDay()]++; daysWithData++;
            }
            if (daysWithData >= 21) {
                const wdAvg = wdSum.map((s, i) => (wdN[i] ? s / wdN[i] : 0));
                let remaining = wdAvg[nowD.getDay()] * (1 - fracToday);
                for (let day = today + 1; day <= daysInMonth; day++) {
                    remaining += wdAvg[new Date(ry, rm - 1, day).getDay()];
                }
                projWeekly = revenueGross + remaining;
            }
        }
        const projMethods = [projCurve, projWeekly].filter((x): x is number => x !== null);
        const projGross = projMethods.length ? projMethods.reduce((a, b) => a + b, 0) / projMethods.length : projLinear;
        const projExpenses = 0; // gastos excluidos por ahora
        // PAUTA REAL = costo que acompaña a la venta (20/10/2026): en la proyección y el break-even
        // entra como % de la facturación (adsRatio), no como monto fijo. Mes en curso: ritmo de
        // pauta del mes completo / facturación proyectada (la pauta llega hasta ayer, la venta hasta hoy).
        // Con el fijo viejo (meses sin ad_spend) sigue como antes: marketing entero dentro de los fijos
        // — si no, el break-even daría más bajo de lo real al principio del mes.
        const adsRatio = marketingSource === 'ads'
            ? (isCurrentMonth ? (projGross > 0 ? marketingFull / projGross : 0) : (revenueGross > 0 ? marketing / revenueGross : 0))
            : 0;
        const simRatio = cmRatio - adsRatio;                       // contribución después de pauta / facturación
        const monthlyCosts = (marketingSource === 'ads' ? fixedSinMkt : fixedAll) + canjeFull;
        const projResult = projGross * simRatio - monthlyCosts;
        // Break-even: facturación bruta mensual que deja resultado 0 con esta estructura
        const breakEven = simRatio > 0 ? monthlyCosts / simRatio : 0;
        const contributionAfterAds = contribution - marketing - canjeCost;
        const roas = marketing > 0 ? revenueGross / marketing : null;

        const productRows = Array.from(byProduct.values()).sort((a, b) => b.net - a.net);
        const toCost = productRows.filter(r => !r.covered).slice(0, 15);

        return {
            mOrders: mOrders.length, revenueGross, revenueNet, ivaDebito: revenueGross - revenueNet, cmv, grossMargin, fees, feesCard, feesClean, feesTN, feesIIBB, shippingOwn, shippingCabify, waCost, waCostUSD: wa?.costUSD ?? null, waCounts: wa?.counts ?? null, waNewModel: wa?.newModel ?? true, contribution,
            fixedTotal, fixedSinMkt, marketing, marketingFull, marketingDaily, elapsedDaysMonth, fixedAll, marketingSource, adsMeta, adsGoogle, adsThroughDay, canjeCost, canjeFull, canjeCount, canjeList, feesML, fixedFromHistory: Array.isArray(fixedAt), expByCat, expensesTotal, result, coverage,
            productRows, toCost,
            cmRatio, adsRatio, simRatio, contributionAfterAds, roas, isCurrentMonth, elapsedFrac, daysInMonth, projGross, projExpenses, projResult, breakEven, monthlyCosts,
            projCurve, projWeekly, projLinear,
            suiteGross, feesSuite,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resMonth, orders, products, fixedCosts, expenses, settings, itemCostAt, suiteSales, waUsage, adSpend, cabifyCost, canjeOrders, versionAt]);

    const saveSuiteSale = async (month: string, amount: number) => {
        setSuiteSales(s => ({ ...s, [month]: amount }));
        await supabase.from('suite_sales').upsert({ month, amount_ars: amount, updated_at: new Date().toISOString() });
    };


    useEffect(() => {
        if (openExpenseModalProp) {
            setActiveTab('expenses');
            setShowExpenseModal(true);
            onExpenseModalClosed?.();
        }
    }, [openExpenseModalProp, onExpenseModalClosed]);

    useEffect(() => {
        if (userEmail === 'martin@krilic.com.ar' || userEmail === 'stephanie@krilic.com.ar') {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    if (userEmail !== 'martin@krilic.com.ar' && userEmail !== 'stephanie@krilic.com.ar') {
        return (
            <div className="costs-denied">
                <AlertCircle size={48} />
                <h2>Acceso Denegado</h2>
                <p>Esta sección es exclusiva para la administración.</p>
            </div>
        );
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            const [settingsRes, materialsRes, fixedRes, productsRes, variantsRes, pmRes, vmRes, expensesRes, providersRes, suiteRes, histRes] = await Promise.all([
                supabase.from('cost_settings').select('*').single(),
                supabase.from('cost_materials').select('*').order('name'),
                supabase.from('cost_fixed').select('*').order('name'),
                supabase.from('tn_products').select('id, name, direct_cost, direct_cost_currency').eq('active', true),
                (async () => {
                    // Paginar tn_variants (hard cap 1000 por request en PostgREST).
                    const all: any[] = [];
                    for (let offset = 0; offset < 20000; offset += 1000) {
                        const { data, error } = await supabase
                            .from('tn_variants')
                            .select('id, product_id, sku, price, values, direct_cost, direct_cost_currency')
                            .order('id', { ascending: true })
                            .range(offset, offset + 999);
                        if (error) return { data: null, error };
                        if (!data || data.length === 0) break;
                        all.push(...data);
                        if (data.length < 1000) break;
                    }
                    return { data: all, error: null as any };
                })(),
                supabase.from('tn_product_materials').select('*'),
                supabase.from('tn_variant_materials').select('*'),
                supabase.from('expenses').select('*').order('created_at', { ascending: false }),
                supabase.from('providers').select('id, name').order('name'),
                supabase.from('suite_sales').select('*'),
                // versiones de costos (migr 103): paginado, puede crecer con cada cambio de receta
                (async () => {
                    const all: any[] = [];
                    for (let offset = 0; offset < 200000; offset += 1000) {
                        const { data, error } = await supabase.from('cost_history').select('entity, ref_id, data, valid_from')
                            .order('id', { ascending: true }).range(offset, offset + 999);
                        if (error || !data?.length) break;
                        all.push(...data);
                        if (data.length < 1000) break;
                    }
                    return { data: all };
                })(),
            ]);

            if (settingsRes.data) setSettings(settingsRes.data);
            if (materialsRes.data) setMaterials(materialsRes.data);
            if (fixedRes.data) setFixedCosts(fixedRes.data);
            if (expensesRes.data) setExpenses(expensesRes.data);
            if (providersRes.data) setProviders(providersRes.data);
            setCostHistory(histRes.data || []);
            if (suiteRes.data) setSuiteSales(Object.fromEntries(suiteRes.data.map((r: any) => [r.month, Number(r.amount_ars) || 0])));

            if (productsRes.data && variantsRes.data) {
                const fullProducts: Product[] = productsRes.data.map((p: any) => {
                    const commonMats = (pmRes.data || []).filter((pm: any) => pm.tn_product_id === p.id);
                    const variants = variantsRes.data
                        .filter((v: any) => v.product_id === p.id)
                        .map((v: any) => ({
                            id: v.id,
                            product_id: v.product_id,
                            name: v.sku || 'Estándar',
                            sku: v.sku || null, // lo usa el cruce por SKU de buildItemCost (antes faltaba)
                            price: Number(v.price) || 0,
                            values: v.values,
                            direct_cost: v.direct_cost,
                            direct_cost_currency: v.direct_cost_currency,
                            materials: (vmRes.data || []).filter((vm: any) => vm.tn_variant_id === v.id)
                        }));
                    return { id: p.id, name: p.name, direct_cost: p.direct_cost, direct_cost_currency: p.direct_cost_currency, variants, materials: commonMats } as any;
                });
                setProducts(fullProducts);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateSettings = async (newSettings: CostSettings) => {
        // Asegurar id=1 y sacar usd_rate_updated_at (Postgres lo gestiona) para evitar
        // conflictos con timestamps o columnas que el cliente no tiene que pisar.
        const { usd_rate_updated_at: _ignored, ...rest } = newSettings as any;
        const payload = { ...rest, id: rest.id ?? 1 };
        const { error } = await supabase.from('cost_settings').upsert(payload, { onConflict: 'id' });
        if (error) {
            console.error('Error guardando settings:', error);
            setSaveMsg(`Error: ${error.message}`);
            setTimeout(() => setSaveMsg(''), 6000);
            return;
        }
        setSettings({ ...newSettings, ...payload });
        setSaveMsg('Guardado ✓');
        setTimeout(() => setSaveMsg(''), 2000);
    };

    const handleAddFixedCost = async () => {
        if (!newFixedName || !newFixedAmount) return;
        const newCost = { name: newFixedName, amount_ars: Number(newFixedAmount) };
        const { data } = await supabase.from('cost_fixed').insert(newCost).select().single();
        if (data) {
            setFixedCosts([...fixedCosts, data as FixedCost]);
            setNewFixedName('');
            setNewFixedAmount('');
            setShowNewFixedForm(false);
        }
    };

    const handleDeleteFixedCost = async (id: number) => {
        if (!window.confirm('¿Eliminar este costo fijo?')) return;
        await supabase.from('cost_fixed').delete().eq('id', id);
        setFixedCosts(fixedCosts.filter(f => f.id !== id));
    };

    const handleSaveExpense = async () => {
        if (!expenseForm.amount) return;
        const provider = expenseForm.provider_id ? providers.find(p => p.id === Number(expenseForm.provider_id)) : null;
        const payload = {
            amount: Number(expenseForm.amount),
            provider_id: provider ? provider.id : null,
            provider_name: provider ? provider.name : null,
            payment_method: expenseForm.payment_method,
            notes: expenseForm.notes || null,
            category: expenseForm.category || 'otros',
            expense_date: expenseForm.expense_date || new Date().toISOString().slice(0, 10),
            created_by: userEmail || null
        };

        if (editingExpenseId) {
            const { data } = await supabase.from('expenses').update(payload).eq('id', editingExpenseId).select().single();
            if (data) {
                setExpenses(expenses.map(e => e.id === editingExpenseId ? data as Expense : e));
            }
        } else {
            const { data } = await supabase.from('expenses').insert(payload).select().single();
            if (data) {
                setExpenses([data as Expense, ...expenses]);
            }
        }
        closeExpenseModal();
    };

    const openEditExpense = (exp: Expense) => {
        setEditingExpenseId(exp.id);
        setExpenseForm({
            amount: String(exp.amount),
            provider_id: exp.provider_id ? String(exp.provider_id) : '',
            payment_method: exp.payment_method,
            notes: exp.notes || '',
            category: (exp as any).category || 'otros',
            expense_date: (exp as any).expense_date || new Date(exp.created_at).toISOString().slice(0, 10)
        });
        setShowExpenseModal(true);
    };

    const closeExpenseModal = () => {
        setShowExpenseModal(false);
        setEditingExpenseId(null);
        setExpenseForm({ amount: '', provider_id: '', payment_method: 'efectivo', notes: '', category: 'otros', expense_date: new Date().toISOString().slice(0, 10) });
    };

    const handleDeleteExpense = async (id: number) => {
        if (!window.confirm('¿Eliminar este gasto?')) return;
        await supabase.from('expenses').delete().eq('id', id);
        setExpenses(expenses.filter(e => e.id !== id));
    };

    const handleAddProvider = async () => {
        if (!newProviderName.trim()) return;
        const { data } = await supabase.from('providers').insert({ name: newProviderName.trim() }).select().single();
        if (data) {
            setProviders([...providers, data as Provider].sort((a, b) => a.name.localeCompare(b.name)));
            setExpenseForm({ ...expenseForm, provider_id: String(data.id) });
            setNewProviderName('');
            setShowNewProvider(false);
        }
    };

    const calculateProduct = () => {
        if (!settings) return;

        let totalVariableCost = 0;
        const materialBreakdown: any[] = [];

        selectedMaterials.forEach(item => {
            const material = materials.find(m => m.id === item.id);
            if (!material) return;

            let cost = 0;
            if (material.type === 'simple') {
                let unitCost = material.unit_cost;
                if (material.currency === 'USD') unitCost *= settings.usd_rate;
                cost = unitCost * item.quantity;
            } else if (material.type === 'acrylic') {
                const sheetAreaMm2 = (material.width_mm || 1220) * (material.height_mm || 2440);
                const sheetWeight = material.weight_kg || 11.4;

                let pricePerKg = material.unit_cost;
                if (material.currency === 'USD') pricePerKg *= settings.usd_rate;

                const pricePerSheet = pricePerKg * sheetWeight * (1 + settings.iva_percent / 100);
                const costPerMm2 = pricePerSheet / sheetAreaMm2;

                const requestedArea = (item.width_mm || 0) * (item.height_mm || 0);
                const areaWithWaste = requestedArea * (1 + settings.acrylic_waste_percent / 100);

                cost = costPerMm2 * areaWithWaste * item.quantity;
            }

            materialBreakdown.push({ ...material, ...item, cost });
            totalVariableCost += cost;
        });

        const totalFixedCosts = fixedCosts.reduce((sum, fc) => sum + fc.amount_ars, 0);

        const pvp = totalVariableCost * (1 + markup / 100);
        const pvpWithTax = pvp * (1 + settings.iva_percent / 100);

        const contributionMargin = pvp - totalVariableCost;
        const breakEvenUnits = contributionMargin > 0 ? totalFixedCosts / contributionMargin : Infinity;

        const profitMarginPercent = contributionMargin / pvp;
        const roasBreakEven = profitMarginPercent > 0 ? 1 / profitMarginPercent : 0;

        setCalculationResult({
            totalVariableCost,
            totalFixedCosts,
            pvp,
            pvpWithTax,
            contributionMargin,
            breakEvenUnits,
            roasBreakEven,
            materialBreakdown
        });
    };

    const fmtARS = (n: number) => n.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    if (loading) {
        return (
            <div className="costs-loading">
                <div className="spinner" />
                <p>Cargando datos de costos...</p>
            </div>
        );
    }

    return (
        <div className="costs-container">
            {/* Navegación de Costos & BI: pestañas agrupadas (Análisis · Plata · Herramientas) */}
            <nav className="tw mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line pb-3" aria-label="Secciones de Costos & BI">
                {(['Análisis', 'Plata', 'Herramientas'] as const).map((g, gi) => (
                    <div key={g} className="flex flex-wrap items-center gap-1">
                        {gi > 0 && <span className="mr-2 hidden h-5 w-px bg-[var(--border)] sm:block" aria-hidden="true" />}
                        <span className="mr-1 text-[length:var(--text-xs)] font-medium uppercase tracking-wide text-faint">{g}</span>
                        {COSTS_TABS.filter(t => t.group === g).map(t => {
                            const on = activeTab === t.key;
                            const Icon = t.icon;
                            return (
                                <button key={t.key} type="button" aria-current={on ? 'page' : undefined} onClick={() => setActiveTab(t.key as CostsTab)}
                                    className={cn('inline-flex h-8 items-center gap-1.5 rounded-[var(--radius-control)] px-2.5 text-[length:var(--text-sm)] transition-colors',
                                        on ? 'bg-[var(--foreground)] font-medium text-[var(--card)]' : 'text-soft hover:bg-muted hover:text-ink')}>
                                    <Icon size={15} />{t.label}
                                </button>
                            );
                        })}
                    </div>
                ))}
            </nav>

            {activeTab === 'imports' && (
                <Imports userEmail={userEmail} />
            )}

            {activeTab === 'investments' && (
                <Investments userEmail={userEmail} />
            )}

            {activeTab === 'tn_import' && (
                <TnImport userEmail={userEmail} />
            )}

            {activeTab === 'financiera' && (
                <div className="tw w-full min-w-0"><FinancieraView onGoTo={t => setActiveTab(t as any)} /></div>
            )}
            {activeTab === 'conciliacion' && (
                <Conciliacion getBucket={getPaymentBucket} />
            )}

            {activeTab === 'cabify' && <CabifyCostsView />}

            {/* STATS */}
            {activeTab === 'resultados' && (
                <ResultadosView r={resultados} resMonth={resMonth} setResMonth={setResMonth}
                    suiteValue={suiteDraft !== '' ? suiteDraft : (suiteSales[resMonth] || '')}
                    onSuiteChange={setSuiteDraft}
                    onSuiteCommit={() => { if (suiteDraft !== '') { saveSuiteSale(resMonth, Number(suiteDraft) || 0); setSuiteDraft(''); } }}
                    simRevenue={simRevenue} setSimRevenue={setSimRevenue} />
            )}

            {activeTab === 'stats' && (() => {
                const { timeline, pieData, paymentData, filteredCount, prevTotals, hasPrev, pendingOrders } = statsData;
                const totalRev = timeline.reduce((s, d) => s + d.total, 0);
                const totalShip = timeline.reduce((s, d) => s + d.shipping, 0);
                const totalBonificado = timeline.reduce((s, d) => s + (d.bonificado || 0), 0);
                const totalPromo = timeline.reduce((s, d) => s + (d.promoDiscount || 0), 0);
                const totalCount = timeline.reduce((s, d) => s + d.count, 0);
                const totalPaid = timeline.reduce((s, d) => s + d.paid, 0);
                const totalPending = timeline.reduce((s, d) => s + d.pending, 0);

                const pctChange = (current: number, prev: number) => {
                    if (prev === 0) return current > 0 ? 100 : 0;
                    return ((current - prev) / prev) * 100;
                };

                // Variación vs el período anterior: misma regla que antes (solo con rango
                // acotado, sin mes puntual y si hubo pedidos en el período anterior).
                const showCmp = hasPrev && statsRangeFilter !== 'all' && statsMonthFilter === 'all';
                const chg = (current: number, prev: number, invert = false): KpiChange | null => showCmp ? {
                    pct: pctChange(current, prev),
                    invert,
                    title: statsRangeFilter === 'month'
                        ? `vs mes anterior al mismo día (${ars(prev)})`
                        : `vs período anterior (${ars(prev)})`,
                } : null;

                const CHANNEL_LABELS: Record<string, string> = {
                    'tiendanube': 'Tiendanube',
                    'mercadolibre': 'MercadoLibre',
                    'woocommerce': 'Boutique',
                    'showroom': 'Showroom',
                    'whatsapp': 'WhatsApp',
                    'ig': 'Instagram',
                    'manual': 'Manual',
                    'cotizador': 'Cotizador'
                };

                const PAYMENT_BUCKET_LABELS: Record<string, string> = {
                    'efectivo': 'Efectivo',
                    'transferencia': 'Transferencia',
                    'tarjeta': 'Tarjeta / Cuotas',
                    'billetera': 'Billetera (MP)',
                    'otro': 'Otro / Sin dato'
                };

                // Medios de pago se puede ver por facturación ($) o por cantidad de pedidos
                const payByCount = payViewMode === 'pedidos';
                const paymentDataLabeled = paymentData
                    .map(d => ({
                        ...d,
                        chartValue: payByCount ? d.count : d.value,
                        pct: payByCount ? d.countPercent : d.percent
                    }))
                    .filter(d => d.chartValue > 0)
                    .sort((a, b) => b.chartValue - a.chartValue);

                const barData = timeline.map(s => ({
                    name: s.label,
                    total: Math.round(s.total),
                    cobrado: Math.round(s.paid),
                    pendiente: Math.round(s.pending),
                    pedidos: s.count
                }));

                const periodLabel = statsMonthFilter !== 'all'
                    ? (availableMonths.find(([k]) => k === statsMonthFilter)?.[1] || statsMonthFilter)
                    : rangeLabel(statsRangeFilter);
                const pendingSum = pendingOrders.reduce((s: number, o: any) => s + Number(o.total_price || 0), 0);
                const P = monthProjection;

                const channelLabels: Record<string, string> = {
                    'tiendanube': 'TN', 'mercadolibre': 'ML', 'woocommerce': 'BT',
                    'showroom': 'Showroom', 'whatsapp': 'WA', 'ig': 'IG', 'manual': 'Manual'
                };
                const isCashOrder = (o: any) => (o.payment_status === 'pending' || o.payment_status === 'voided') && o.payment_details?.method === 'custom';
                // wa.me necesita el número con código de país UNA vez: los de TN ya traen 54.
                const waNumber = (raw: string) => { const d = String(raw || '').replace(/\D/g, ''); return d.startsWith('54') ? d : '54' + d; };
                const pendingCols: GridCol<any>[] = [
                    { key: 'id', header: 'Pedido', sort: o => getOrderDisplayId(o), render: o => <span className="font-semibold text-ink">#{getOrderDisplayId(o)}</span> },
                    { key: 'name', header: 'Cliente', sort: o => o.contact_name || '', render: o => <span className="block max-w-[14rem] truncate" title={o.contact_name}>{o.contact_name || '—'}</span> },
                    {
                        key: 'phone', header: 'Contacto', sort: o => o.contact_phone || '',
                        render: o => o.contact_phone ? (
                            <span className="inline-flex items-center gap-1">
                                <a href={`https://wa.me/${waNumber(o.contact_phone)}`} target="_blank" rel="noopener noreferrer" title="Escribir por WhatsApp" aria-label="WhatsApp"
                                    className="grid h-7 w-7 place-items-center rounded-[var(--radius-control)] text-[#128c4a] hover:bg-muted"><MessageCircle size={15} /></a>
                                <a href={`tel:${o.contact_phone}`} title="Llamar" aria-label="Llamar"
                                    className="grid h-7 w-7 place-items-center rounded-[var(--radius-control)] text-soft hover:bg-muted"><Phone size={15} /></a>
                                <span className="text-[length:var(--text-xs)] text-faint tabular-nums">{o.contact_phone}</span>
                            </span>
                        ) : <span className="text-faint">Sin teléfono</span>,
                    },
                    { key: 'total', header: 'Monto', align: 'right', sort: o => Number(o.total_price || 0), render: o => <span className="font-semibold">{ars(Number(o.total_price || 0))}</span> },
                    {
                        key: 'type', header: 'Tipo', sort: o => (isCashOrder(o) ? 'Efectivo' : 'Pendiente'), hideOnMobile: true,
                        render: o => isCashOrder(o) ? <Badge size="sm" tone="info">Efectivo</Badge> : <Badge size="sm" tone="warning">Pendiente</Badge>,
                    },
                    { key: 'src', header: 'Canal', sort: o => channelLabels[o.source] || o.source, render: o => <span className="text-soft">{channelLabels[o.source] || o.source}</span>, hideOnMobile: true },
                    { key: 'date', header: 'Fecha', align: 'right', sort: o => o.created_at, render: o => <span className="text-soft">{new Date(o.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}</span> },
                ];

                return (
                    <div className="tw w-full min-w-0 pb-12 pt-1">
                        {showPromos && <Promotions onClose={() => setShowPromos(false)} />}
                        <TabHeader
                            title="Ventas"
                            purpose="Cuánto se vendió y cuánto se cobró en el período, por canal y por medio de pago. No cuenta pedidos cancelados, presupuestos ni stock."
                            actions={<Button variant="secondary" onClick={() => setShowPromos(true)}><Tag size={16} /> Promociones</Button>}
                        />

                        <FilterBar
                            range={statsMonthFilter !== 'all' ? null : statsRangeFilter}
                            onRange={r => { setStatsRangeFilter(r); setStatsMonthFilter('all'); }}
                            month={statsMonthFilter}
                            months={availableMonths}
                            onMonth={m => { setStatsMonthFilter(m); setStatsRangeFilter('all'); }}
                            summary={<>{periodLabel.charAt(0).toUpperCase() + periodLabel.slice(1)} · {filteredCount.toLocaleString('es-AR')} pedidos</>}
                        />

                        {/* Proyección: es del MES COMPLETO y no responde al filtro de período.
                            Va aparte (borde punteado + aclaración) para que no parezca que el filtro no anda. */}
                        <section
                            className="mb-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-[var(--radius-surface)] border border-dashed border-[var(--border-strong)] bg-card px-4 py-3"
                            title={`Facturado ${ars(P.actual)} en ${P.elapsedDays.toFixed(1)} de ${P.daysInMonth} días → run-rate a fin de mes. Mes pasado completo: ${ars(P.prevMonth)}`}
                        >
                            <div className="flex min-w-0 items-center gap-3">
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-control)] bg-muted text-soft"><TrendingUp size={18} /></span>
                                <div className="min-w-0">
                                    <div className="text-[length:var(--text-sm)] text-soft">Proyección de {P.monthLabel}</div>
                                    <div className="text-[length:var(--text-xl)] font-semibold tabular-nums text-ink">{ars(Math.round(P.projected))}</div>
                                </div>
                            </div>
                            <div className="text-[length:var(--text-sm)] text-soft tabular-nums">
                                ~{P.projectedCount.toLocaleString('es-AR')} pedidos · van {ars(P.actual)} en {P.elapsedDays.toLocaleString('es-AR', { maximumFractionDigits: 1 })} de {P.daysInMonth} días
                            </div>
                            {P.pctVsPrev !== null && (
                                <Badge tone={Math.abs(P.pctVsPrev) < 0.1 ? 'neutral' : P.pctVsPrev > 0 ? 'success' : 'danger'}>
                                    <span className="inline-flex items-center gap-1">
                                        {P.pctVsPrev > 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                                        {pct(Math.abs(P.pctVsPrev))} vs mes pasado completo
                                    </span>
                                </Badge>
                            )}
                            <span className="ml-auto text-[length:var(--text-xs)] text-faint">Mes completo · no cambia con el filtro</span>
                        </section>

                        <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Kpi label="Facturación" value={totalRev} prefix="$" change={chg(totalRev, prevTotals.total)} context="Total de los pedidos, envío incluido" />
                            <Kpi label="Cobrado" value={totalPaid} prefix="$" change={chg(totalPaid, prevTotals.paid)} context={totalRev > 0 ? `${pct((totalPaid / totalRev) * 100)} de lo facturado` : 'Pagados o entregados'} />
                            <Kpi label="Pendiente de cobro" value={totalPending} prefix="$" context={`${pendingOrders.length} pedidos sin cobrar · detalle abajo`} />
                            <Kpi label="Pedidos" value={totalCount} change={chg(totalCount, prevTotals.count)} context="Sin cancelados, presupuestos ni stock" />
                            <Kpi label="Ticket promedio" value={totalCount > 0 ? totalRev / totalCount : 0} prefix="$"
                                change={chg(totalCount > 0 ? totalRev / totalCount : 0, prevTotals.count > 0 ? prevTotals.total / prevTotals.count : 0)} context="Facturación ÷ pedidos" />
                            <Kpi label="Envíos cobrados" value={totalShip} prefix="$" change={chg(totalShip, prevTotals.shipping)} context="Lo que pagaron los clientes por el envío" />
                            <Kpi label="Envío bonificado" value={totalBonificado} prefix="$" change={chg(totalBonificado, prevTotals.bonificado, true)} context="Envíos gratis: el costo lo pagás vos" />
                            <Kpi label="Descuento por promos" value={totalPromo} prefix="$" change={chg(totalPromo, prevTotals.promoDiscount, true)} context="Promociones del catálogo de Tiendanube" />
                        </section>

                        <div className="mb-5 grid gap-4 lg:grid-cols-3 lg:items-start">
                            <Panel
                                className="lg:col-span-2"
                                title="Ventas en el tiempo"
                                icon={<BarChart3 size={16} />}
                                subtitle="Cobrado y pendiente de cada período"
                                actions={
                                    <SegmentedControl label="Agrupar por" value={statsViewMode} onValueChange={v => setStatsViewMode(v as 'daily' | 'weekly' | 'monthly')}
                                        options={[{ value: 'daily', label: 'Día' }, { value: 'weekly', label: 'Semana' }, { value: 'monthly', label: 'Mes' }]} />
                                }
                            >
                                {barData.length === 0 ? (
                                    <p className="py-16 text-center text-[length:var(--text-sm)] text-faint">Sin ventas en el período.</p>
                                ) : (
                                    <>
                                        <div className="mb-2 flex flex-wrap gap-4">
                                            <LegendDot color={VIZ.s1} label="Cobrado" />
                                            <LegendDot color={VIZ.s2} label="Pendiente" />
                                        </div>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={barData} margin={{ top: 8, right: 4, left: 0, bottom: 0 }} barCategoryGap="18%">
                                                <CartesianGrid stroke={VIZ.grid} vertical={false} />
                                                <XAxis dataKey="name" tick={{ fill: VIZ.muted, fontSize: 11 }} tickLine={false} axisLine={{ stroke: VIZ.axis }} minTickGap={8} />
                                                <YAxis tick={{ fill: VIZ.muted, fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v: number) => arsCompact(v)} width={62} />
                                                <Tooltip
                                                    cursor={{ fill: 'rgba(11,11,11,0.04)' }}
                                                    content={(props: any) => (
                                                        <ChartTooltip {...props} rows={d => [
                                                            { label: 'Cobrado', value: ars(d.cobrado), color: VIZ.s1 },
                                                            { label: 'Pendiente', value: ars(d.pendiente), color: VIZ.s2 },
                                                            { label: 'Total', value: ars(d.total), strong: true },
                                                            { label: 'Pedidos', value: String(d.pedidos) },
                                                        ]} />
                                                    )}
                                                />
                                                {/* 2px de superficie entre segmentos; solo el de arriba lleva punta redondeada */}
                                                <Bar dataKey="cobrado" stackId="a" fill={VIZ.s1} stroke="#ffffff" strokeWidth={1} maxBarSize={44} />
                                                <Bar dataKey="pendiente" stackId="a" fill={VIZ.s2} stroke="#ffffff" strokeWidth={1} radius={[4, 4, 0, 0]} maxBarSize={44} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </>
                                )}
                            </Panel>

                            <div className="flex min-w-0 flex-col gap-4">
                                <Panel title="Por canal" icon={<Activity size={16} />} subtitle="Facturación de cada canal">
                                    <BarList items={pieData.map(d => ({
                                        key: d.name,
                                        label: CHANNEL_LABELS[d.name] || d.name,
                                        share: Number(d.percent),
                                        value: ars(d.value),
                                        sub: `${d.count} pedidos`,
                                    }))} />
                                </Panel>
                                <Panel
                                    title="Medios de pago"
                                    icon={<Receipt size={16} />}
                                    subtitle={payByCount ? 'Cantidad de pedidos' : 'Facturación'}
                                    actions={
                                        <SegmentedControl label="Medir por" value={payViewMode} onValueChange={v => setPayViewMode(v as 'monto' | 'pedidos')}
                                            options={[{ value: 'monto', label: '$' }, { value: 'pedidos', label: 'Pedidos' }]} />
                                    }
                                >
                                    <BarList items={paymentDataLabeled.map(d => ({
                                        key: d.name,
                                        label: PAYMENT_BUCKET_LABELS[d.name] || d.name,
                                        share: Number(d.pct),
                                        value: payByCount ? `${d.count} ped.` : ars(d.value),
                                    }))} />
                                </Panel>
                            </div>
                        </div>

                        <DataGrid
                            rows={pendingOrders}
                            cols={pendingCols}
                            title="Pendientes de cobro"
                            searchPlaceholder="Buscar pedido, cliente o teléfono…"
                            pageSize={25}
                            emptyTitle="Nada pendiente de cobro"
                            emptyText="Todos los pedidos del período están cobrados."
                            footer={<>{pendingOrders.length} pedidos · {ars(pendingSum)}</>}
                        />
                    </div>
                );
            })()}

            {/* PRODUCT RANKING */}
            {activeTab === 'products' && (() => {
                const { rows, totalQty, totalRevenue, distinct } = productRanking;
                // El # es la posición según "Ranking por" (el orden que trae productRanking);
                // las columnas de la grilla se pueden reordenar sin perder ese puesto.
                const rankRows = rows.map((r, i) => ({ ...r, id: r.name, rank: i + 1 }));
                const top5 = topShare(rows, totalRevenue, 5);
                const maxRev = rows.reduce((m, r) => Math.max(m, r.revenue), 0);
                const cols: GridCol<typeof rankRows[number]>[] = [
                    { key: 'rank', header: '#', align: 'right', width: '3.5rem', sort: r => r.rank, render: r => <span className="font-semibold text-faint">{r.rank}</span> },
                    { key: 'name', header: 'Producto', sort: r => r.name, render: r => <span className="block max-w-[30rem] truncate font-medium text-ink" title={r.name}>{r.name}</span> },
                    { key: 'qty', header: 'Unidades', align: 'right', sort: r => r.qty, render: r => <span className="font-semibold">{r.qty.toLocaleString('es-AR')}</span> },
                    { key: 'avg', header: 'Precio prom.', align: 'right', sort: r => r.avgPrice, render: r => ars(r.avgPrice), hideOnMobile: true },
                    { key: 'rev', header: 'Facturación', align: 'right', sort: r => r.revenue, render: r => <span className="font-medium">{ars(r.revenue)}</span> },
                    { key: 'orders', header: 'Pedidos', align: 'right', sort: r => r.orders, render: r => <span className="text-soft">{r.orders}</span>, hideOnMobile: true },
                    {
                        key: 'share', header: '% del total', align: 'right', width: '10rem', sort: r => r.revenue, hideOnMobile: true,
                        render: r => {
                            const share = totalRevenue > 0 ? (r.revenue / totalRevenue) * 100 : 0;
                            return (
                                <span className="inline-flex w-full items-center justify-end gap-2">
                                    <span className="h-1.5 w-16 overflow-hidden rounded-full bg-[#f0efec]" aria-hidden="true">
                                        <span className="block h-full rounded-full" style={{ width: `${maxRev > 0 ? (r.revenue / maxRev) * 100 : 0}%`, background: VIZ.s1 }} />
                                    </span>
                                    <span className="min-w-[3rem] text-soft">{pct(share)}</span>
                                </span>
                            );
                        },
                    },
                ];
                return (
                    <div className="tw w-full min-w-0 pb-12 pt-1">
                        <TabHeader
                            title="Ranking de productos"
                            purpose="Qué se vende más en el período: unidades, facturación y peso de cada producto. Suma precio × cantidad de cada ítem vendido (sin envío), por eso no coincide con la facturación de Ventas."
                        />
                        <FilterBar range={prodRange} onRange={setProdRange}>
                            <SegmentedControl label="Ranking por" value={prodSort} onValueChange={v => setProdSort(v as 'qty' | 'revenue' | 'avg')}
                                options={[{ value: 'qty', label: 'Unidades' }, { value: 'revenue', label: 'Facturación' }, { value: 'avg', label: 'Precio prom.' }]} />
                            <SegmentedControl label="Agrupar" value={prodGroupBy} onValueChange={v => setProdGroupBy(v as 'parent' | 'variant')}
                                options={[{ value: 'parent', label: 'Por producto' }, { value: 'variant', label: 'Por variante' }]} />
                        </FilterBar>

                        <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                            <Kpi label={prodGroupBy === 'parent' ? 'Productos distintos' : 'Variantes distintas'} value={distinct} context={`Vendidos en ${rangeLabel(prodRange).toLowerCase()}`} />
                            <Kpi label="Unidades vendidas" value={totalQty} context={distinct > 0 ? `${(totalQty / distinct).toLocaleString('es-AR', { maximumFractionDigits: 1 })} por producto en promedio` : 'Sin ventas en el período'} />
                            <Kpi label="Facturación en productos" value={totalRevenue} prefix="$" context="Precio × cantidad de cada ítem, sin envío" />
                            <Kpi label="Peso del top 5" value={top5} decimals={1} suffix="%" context="De la facturación se la llevan los 5 que más facturan" />
                        </section>

                        <DataGrid
                            rows={rankRows}
                            cols={cols}
                            rowKey={r => r.name}
                            title="Productos vendidos"
                            searchPlaceholder="Buscar producto…"
                            pageSize={50}
                            emptyTitle="Sin ventas en este período"
                            emptyText="Probá con un período más largo."
                        />
                    </div>
                );
            })()}

            {/* EXPENSES */}
            {activeTab === 'expenses' && (
                <GastosView expenses={expenses} month={expenseMonthFilter} setMonth={setExpenseMonthFilter}
                    onNew={() => setShowExpenseModal(true)} onEdit={openEditExpense} onDelete={handleDeleteExpense} />
            )}

            {/* Expense Modal */}
            <GastoDialog open={showExpenseModal} editing={!!editingExpenseId}
                form={expenseForm} onChange={patch => setExpenseForm(f => ({ ...f, ...patch }))}
                providers={providers}
                showNewProvider={showNewProvider} setShowNewProvider={setShowNewProvider}
                newProviderName={newProviderName} setNewProviderName={setNewProviderName}
                onAddProvider={handleAddProvider} onSave={handleSaveExpense} onClose={closeExpenseModal} />

            {/* DASHBOARD */}
            {activeTab === 'dashboard' && (() => {
                // Compute all metrics from product data
                const allVariants = products.flatMap(p =>
                    p.variants.map(v => {
                        const calc = calcCost(v.materials, p.materials);
                        const cost = calc.cost;
                        const pvp = v.price || 0;
                        const pvpNet = pvp / (1 + (settings?.iva_percent || 21) / 100);
                        const margin = pvpNet - cost;

                        return {
                            productName: p.name,
                            variantName: v.name,
                            fullName: `${p.name} — ${v.name}`,
                            markup: cost > 0 ? (margin / cost) * 100 : 0,
                            cost: cost,
                            pvp: pvpNet,
                            pvpWithTax: pvp,
                            margin: margin,
                            marginPercent: pvpNet > 0 ? (margin / pvpNet) * 100 : 0,
                            roasBreakEven: pvpNet > 0 && margin > 0 ? pvpNet / margin : 0,
                            materialCount: v.materials.length,
                        };
                    })
                ).filter(v => v.cost > 0 && v.pvp > 0);

                const totalFixedMonthly = fixedCosts.reduce((s, f) => s + f.amount_ars, 0);
                const avgMarkup = allVariants.length > 0 ? allVariants.reduce((s, v) => s + v.markup, 0) / allVariants.length : 0;
                const avgMargin = allVariants.length > 0 ? allVariants.reduce((s, v) => s + v.marginPercent, 0) / allVariants.length : 0;
                const avgRoas = allVariants.length > 0 ? allVariants.reduce((s, v) => s + v.roasBreakEven, 0) / allVariants.length : 0;
                const avgPvp = allVariants.length > 0 ? allVariants.reduce((s, v) => s + v.pvp, 0) / allVariants.length : 0;
                const avgCost = allVariants.length > 0 ? allVariants.reduce((s, v) => s + v.cost, 0) / allVariants.length : 0;

                // Top products by margin
                const sortedByMargin = [...allVariants].sort((a, b) => b.margin - a.margin);
                const top3Margin = sortedByMargin.slice(0, 3);
                const bottom3Margin = sortedByMargin.slice(-3).reverse();

                // Top by margin %
                const sortedByMarginPct = [...allVariants].sort((a, b) => b.marginPercent - a.marginPercent);

                const maxMargin = allVariants.length > 0 ? Math.max(...allVariants.map(v => v.marginPercent)) : 0;

                // Bandas de escalabilidad (mismas reglas de siempre: ≤2 / 2–4 / >4)
                const bands = bandCounts(allVariants);
                const BAND_COLOR = { high: '#0ca30c', medium: '#fab219', low: '#d03b3b' } as const;
                const showBottom = bottom3Margin.length > 0 && bottom3Margin[0].margin !== top3Margin[top3Margin.length - 1]?.margin;

                const MarginRow = ({ v, rank, low }: { v: typeof allVariants[number]; rank: React.ReactNode; low?: boolean }) => (
                    <li className="flex min-w-0 items-center gap-3">
                        <span className={cn('grid h-7 w-7 shrink-0 place-items-center rounded-full text-[length:var(--text-xs)] font-semibold',
                            low ? 'bg-[color-mix(in_oklab,var(--danger)_10%,var(--card))] text-[var(--danger)]' : 'bg-muted text-ink')}>{rank}</span>
                        <div className="min-w-0 flex-1">
                            <div className="truncate text-[length:var(--text-sm)] font-medium text-ink" title={v.fullName}>{v.fullName}</div>
                            {!low && (
                                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-[#f0efec]" aria-hidden="true">
                                    <div className="h-full rounded-full" style={{ width: `${maxMargin > 0 ? (v.marginPercent / maxMargin) * 100 : 0}%`, background: VIZ.s1 }} />
                                </div>
                            )}
                        </div>
                        <div className="shrink-0 text-right tabular-nums">
                            <div className="text-[length:var(--text-sm)] font-semibold text-ink">{ars(v.margin)}</div>
                            <div className="text-[length:var(--text-xs)] text-faint">{pct(v.marginPercent)}</div>
                        </div>
                    </li>
                );

                return (
                    <div className="tw w-full min-w-0 pb-12 pt-1">
                        <TabHeader
                            title="Rentabilidad del catálogo"
                            purpose="Margen y ROAS de equilibrio de cada variante con costo cargado, sobre el precio de lista sin IVA. Para decidir qué empujar en anuncios y qué precio revisar."
                        />
                        {allVariants.length === 0 ? (
                            <div className="rounded-[var(--radius-surface)] border border-line bg-card shadow-xs">
                                <EmptyState
                                    icon={<Activity size={20} />}
                                    title="Todavía no hay variantes con costo"
                                    description="Asigná insumos a las variantes desde Catálogo (panel de cada producto) y acá vas a ver su margen y ROAS."
                                />
                            </div>
                        ) : (
                            <>
                                <section className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                                    <Kpi label="Productos" value={products.length} context={`${allVariants.length.toLocaleString('es-AR')} variantes con costo y precio`} />
                                    <Kpi label="Margen promedio" value={avgMargin} decimals={1} suffix="%" context={`Sobre el PVP sin IVA · markup promedio ${pct(avgMarkup, 0)}`} />
                                    <Kpi label="ROAS de equilibrio" value={avgRoas} decimals={2} suffix="x" context="Promedio: lo mínimo que tienen que devolver los anuncios para no perder" />
                                    <Kpi label="Costos fijos por mes" value={totalFixedMonthly} prefix="$" context="Suma de los costos fijos cargados (ARS)" />
                                </section>

                                <div className="mb-5 grid gap-4 lg:grid-cols-3">
                                    <Panel title="Más margen por unidad" icon={<Award size={16} />} subtitle="Los que más conviene empujar en anuncios">
                                        <ol className="m-0 flex list-none flex-col gap-3 p-0">
                                            {top3Margin.map((v, i) => <MarginRow key={i} v={v} rank={i + 1} />)}
                                        </ol>
                                    </Panel>
                                    <Panel title="Menos margen por unidad" icon={<AlertCircle size={16} />} subtitle="Revisar precio o costo">
                                        {showBottom ? (
                                            <ol className="m-0 flex list-none flex-col gap-3 p-0">
                                                {bottom3Margin.map((v, i) => <MarginRow key={i} v={v} rank="↓" low />)}
                                            </ol>
                                        ) : <p className="text-[length:var(--text-sm)] text-faint">Todas las variantes tienen un margen parecido.</p>}
                                    </Panel>
                                    <Panel title="Escalabilidad con anuncios" icon={<Target size={16} />} subtitle="Según el ROAS que hace falta para no perder">
                                        {/* barra 100% apilada: cuántas variantes caen en cada banda */}
                                        <div className="mb-4 flex h-2.5 w-full gap-[2px] overflow-hidden rounded-full" aria-hidden="true">
                                            {(['high', 'medium', 'low'] as const).map(b => bands[b] > 0 && (
                                                <div key={b} style={{ flex: bands[b], background: BAND_COLOR[b] }} />
                                            ))}
                                        </div>
                                        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
                                            {(['high', 'medium', 'low'] as const).map(b => (
                                                <li key={b} className="flex items-center justify-between gap-3 text-[length:var(--text-sm)]">
                                                    <span className="flex min-w-0 items-center gap-2">
                                                        <Badge size="sm" tone={BAND_META[b].tone}><span className="inline-flex items-center gap-1">{BAND_META[b].icon}{BAND_META[b].label}</span></Badge>
                                                        <span className="truncate text-faint">{BAND_META[b].rule}</span>
                                                    </span>
                                                    <span className="shrink-0 tabular-nums text-ink">
                                                        <strong className="font-semibold">{bands[b]}</strong>
                                                        <span className="ml-2 inline-block min-w-[3.25rem] text-right text-faint">{pct((bands[b] / allVariants.length) * 100, 0)}</span>
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    </Panel>
                                </div>

                                <AnalisisPortfolio rows={sortedByMarginPct} />

                                <div className="mt-4 flex items-start gap-2 rounded-[var(--radius-control)] border border-line bg-muted px-3 py-2.5 text-[length:var(--text-sm)] text-soft">
                                    <Zap size={16} className="mt-0.5 shrink-0 text-faint" />
                                    <span>
                                        PVP promedio <strong className="text-ink">{ars(avgPvp)}</strong> con costo promedio <strong className="text-ink">{ars(avgCost)}</strong>.
                                        {avgRoas < 3
                                            ? ` Con un ROAS de equilibrio promedio de ${avgRoas.toFixed(2)}x, hay margen para escalar con anuncios.`
                                            : ` Con un ROAS de equilibrio promedio de ${avgRoas.toFixed(2)}x, conviene subir precios o bajar costos antes de escalar fuerte con anuncios.`}
                                    </span>
                                </div>
                            </>
                        )}
                    </div>
                );
            })()}

            {/* CALCULATOR */}
            {activeTab === 'calculator' && (
                <CalcPanel
                    materials={materials}
                    items={selectedMaterials}
                    setItems={setSelectedMaterials}
                    productName={productName}
                    setProductName={setProductName}
                    markup={markup}
                    setMarkup={setMarkup}
                    result={calculationResult}
                    onCalculate={calculateProduct}
                    fmtARS={fmtARS}
                    usdRate={settings?.usd_rate}
                    ivaPercent={settings?.iva_percent}
                    wastePercent={settings?.acrylic_waste_percent}
                />
            )}

            {/* SETTINGS */}
            {activeTab === 'settings' && settings && (
                <ConfigPanel settings={settings} setSettings={setSettings} onSave={() => handleUpdateSettings(settings)} saveMsg={saveMsg} />
            )}

            {/* FIXED COSTS */}
            {activeTab === 'fixed' && (
                <FijosPanel
                    rows={fixedCosts}
                    setRows={setFixedCosts}
                    onDelete={handleDeleteFixedCost}
                    showNew={showNewFixedForm}
                    setShowNew={setShowNewFixedForm}
                    newName={newFixedName}
                    setNewName={setNewFixedName}
                    newAmount={newFixedAmount}
                    setNewAmount={setNewFixedAmount}
                    onAdd={handleAddFixedCost}
                />
            )}
                    </div>
    );
}
